import axios from "axios"
import { config } from "Utils/config"
import { v4 as uuidv4 } from "uuid"
import { sanitizeFileName } from "Shared/utils/sanitizeString"

/**
 * Taille maximale d'un fichier déposé (Mo). Même valeur que `MAX_UPLOAD_SIZE_MB` côté back
 * (charts/values.yaml § env.config), qui reste la seule garantie : ce contrôle évite seulement
 * d'envoyer des centaines de Mo pour se voir refuser le fichier à la fin.
 */
export const MAX_UPLOAD_SIZE_MB = 200

/** Refus décidé côté front, avant tout envoi. `code` est une clé de traduction. */
export class UploadRejectedError extends Error {
  constructor(public readonly code: string) {
    super(code)
    this.name = "UploadRejectedError"
  }
}

/**
 * Clé de traduction d'un échec de `uploadFile` : code renvoyé par le back (`code_error`, par ex.
 * `FILE_TOO_LARGE`, `UPLOAD_SCAN_UNAVAILABLE`, `FILE_EXTENSION_NOT_ACCEPTED`) ou refus front.
 */
export const uploadErrorCode = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { code_error?: string; code?: string } | undefined
    return data?.code_error || data?.code || "UNKNOWN_ERROR"
  }
  if (error instanceof UploadRejectedError) return error.code
  return "UNKNOWN_ERROR"
}

export type idsType = {
  referentialId?: string
  sampleId?: string

  workflowId?: string
  workflowFor?: string

  runId?: string
  evaluationId?: string
  occurenceId?: string
  dossierId?: string

  conversationId?: string
}

const initiateMultipartUpload = async (filename: string, token: string) => {
  try {
    const response = await axios.post(
      `${config.APOLLO_SERVER_URL}/file/upload/initiate`,
      { filename },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )

    if (response.status === 200 || response.status === 201) {
      return response.data
    } else {
      throw new Error(`Unexpected status code: ${response.status}`)
    }
  } catch (error: any) {
    console.error("Erreur lors de l'initiation de l'upload multipart :", error.message)
    throw error
  }
}

const uploadPart = async (
  uploadId: string,
  partNumber: number,
  partBlob: any,
  filename: string,
  token: string,
  maxRetries: number = 3
) => {
  const formData = new FormData()
  formData.append("file", partBlob)
  let attempts = 0

  while (attempts < maxRetries) {
    try {
      const response = await axios.post(`${config.APOLLO_SERVER_URL}/file/upload/part`, formData, {
        params: {
          uploadId,
          partNumber,
          filename,
        },
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 200 || response.status === 201) {
        return response.data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`)
      }
    } catch (error: any) {
      attempts++
      console.error(
        `Erreur lors de l'upload de la part ${partNumber} (Tentative ${attempts}) :`,
        error.message
      )

      if (attempts >= maxRetries) {
        throw new Error(
          `Echec de l'upload de la part ${partNumber} après ${maxRetries} tentatives: ${error.message}`
        )
      }
    }
  }
}

const completeMultipartUpload = async (
  uploadId: string,
  filename: string,
  body: any,
  token: string
) => {
  const response = await axios.post(`${config.APOLLO_SERVER_URL}/file/upload/complete`, body, {
    params: { uploadId, filename },
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  })

  return response
}

export const uploadFile = async (file: any, token: string) => {
  // const chunkSize = 1024 * 1024 * 1024 // 1Gb
  const chunkSize = 50 * 1024 * 1024 // 50mb
  // const chunkSize = 5 * 1024 * 1024 // 5mb
  if (typeof file?.size === "number" && file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
    throw new UploadRejectedError("FILE_TOO_LARGE")
  }
  const filename = `${uuidv4()}-${sanitizeFileName(file.name)}`
  const totalParts = Math.ceil(file.size / chunkSize)

  try {
    // Initier le multipart upload
    const { UploadId } = await initiateMultipartUpload(filename, token)
    const parts = []
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      const start = (partNumber - 1) * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      const partBlob = file.slice(start, end)

      // Télécharger chaque partie
      parts.push(
        await uploadPart(UploadId, partNumber, partBlob, filename, token).then(response => {
          const etag = response.headers?.etag || response.data?.ETag || response?.ETag

          if (!etag) {
            throw new Error("ETag missing from response")
          }

          return { ETag: etag, PartNumber: partNumber }
        })
      )
    }

    // Attendre que toutes les parties soient téléchargées
    // const parts = await Promise.all(promises)

    // Compléter le multipart upload
    const {
      data: { fileId },
      status,
    } = await completeMultipartUpload(UploadId, filename, { parts }, token)

    // Retourner l'ID du fichier et le statut final
    return { filename, UploadId, fileId, status }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      throw error // <— crucial
    }

    throw new Error(`Erreur lors du téléchargement du fichier : ${error}`)
  }
}

export async function fetchFileFromApi(idFile: string, token: string): Promise<any> {
  const response = await axios.get(`${config.APOLLO_SERVER_URL}/file/read/${idFile}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: "blob",
  })

  return {
    data: response.data,
    contentType: response.headers["content-type"],
  }
}
